import React, { PropTypes, Component } from 'react'
import { Link } from 'react-router'
import { connect } from 'react-redux'
import Helmet from 'react-helmet'
import classes from './Cart.scss'
import moment from 'moment'

// Actions
import { orderActions, userActions } from 'redux/actionCreators'

// Components
import { FormGroup, Loader, ProductModal, GiveawayItem, Heading } from 'components'

// Helpers
import { get, isEqual, clone, debounce, groupBy, sumBy } from 'lodash'
import { validateRequired, hasError } from 'helpers/formHelpers'
import { formatMoney, isNotNumber, apiFetch } from 'helpers/utilities'

// ยอดรวมตะกร้าขั้นต่ำที่จะแยกแสดงสินค้าเป็นกลุ่มจัดส่งฟรี / มีค่าจัดส่ง
const FREE_DELIVERY_THRESHOLD = 3000

const mapStateToProps = (state) => ({
  attempting: get(state, 'order.checkoutAttempting') || false,
  checkedOut: get(state, 'order.checkoutAttempted') || false,
  cartProducts: get(state, 'order.cartProducts') || [],
  cartOrderId: get(state, 'user.me.cartOrderId') || get(state, 'order.currentOrderId') || '',
  contactPersonName: get(state, 'user.me.contactPersonName') || '',
  address: get(state, 'user.me.address') || '',
  mobile: get(state, 'user.me.mobile') || '',
  userClass: get(state, 'user.me.userClass') || 0,
  giveaways: get(state, 'user.giveaways') || [],
  checkoutError: get(state, 'order.error')
})
const mapDispatchToProps = (dispatch) => ({
  attemptGetCart: () =>
    dispatch(orderActions.attemptGetCart()),
  attemptSaveCart: (products) =>
    dispatch(orderActions.attemptSaveCart({ products })),
  attemptCheckout: (payload) =>
    dispatch(orderActions.attemptCheckout(payload)),
  attemptAddToWishlist: (productCode) =>
    dispatch(userActions.attemptAddToWishlist({ productCode })),
  attemptRemoveFromWishlist: (productCode) =>
    dispatch(userActions.attemptRemoveFromWishlist({ productCode })),
  attemptFindGiveaways: () =>
    dispatch(userActions.attemptFindGiveaways()),
  openProductModal: (productKey, amount) =>
    dispatch({ type: 'PRODUCT_MODAL_OPEN', productKey, amount }),
  setInitial: () => dispatch({ type: 'SET_INITIAL' })
})

@connect(mapStateToProps, mapDispatchToProps)
class Cart extends Component {
  static propTypes = {
    cartProducts: PropTypes.array.isRequired,
    attemptSaveCart: PropTypes.func.isRequired,
    userClass: PropTypes.number.isRequired
  };

  static contextTypes = {
    router: PropTypes.object.isRequired
  };

  constructor(props) {
    super(props)
    this.state = {
      step: 1,
      checkedOut: false,
      checked: true,
      amounts: this.getAmounts(props.cartProducts),
      selectedProductKeys: [],
      modalVisible: false,
      currentProduct: null,
      contactPersonName: props.contactPersonName || '',
      address: props.address || '',
      mobile: props.mobile || ''
    }
  }

  componentDidMount() {
    window.objectFitImages()
    this.props.attemptFindGiveaways()
    this.props.attemptGetCart()
  }

  componentWillReceiveProps(nextProps) {
    let nextState = {}
    if (!isEqual(this.props.cartProducts, nextProps.cartProducts)) {
      nextState.amounts = this.getAmounts(nextProps.cartProducts)
    }
    if (!this.props.checkedOut && nextProps.checkedOut) {
      nextState.checkedOut = true
    }
    if (!this.props.contactPersonName && nextProps.contactPersonName) {
      nextState.contactPersonName = nextProps.contactPersonName
    }
    if (!this.props.address && nextProps.address) {
      nextState.address = nextProps.address
    }
    if (!this.props.mobile && nextProps.mobile) {
      nextState.mobile = nextProps.mobile
    }
    if (!this.props.checkoutError && nextProps.checkoutError) {
      nextState.checkoutError = nextProps.checkoutError;
    }
    if (nextState !== {}) this.setState(nextState)
  }

  getAmounts = (products = []) =>
    products.reduce((map, product) => ({
      ...map,
      [`${product.productCode}-${product.unitId}`]: parseInt(product.amount, 10)
    })
      , {})

  _onMouseEnter = (productKey) => { this.setState({ [`${productKey}-hovered`]: true }) }
  _onMouseLeave = (productKey) => { this.setState({ [`${productKey}-hovered`]: false }) }

  _onSelectProduct = (productKey) => {
    this.setState({
      selectedProductKeys: (this.state.selectedProductKeys || []).indexOf(productKey) > -1
        ? this.state.selectedProductKeys.filter(key => key !== productKey)
        : this.state.selectedProductKeys.concat([productKey])
    })
  }

  _onAmountChange = (productCode, stockAmount) =>
    (e) => {
      if (e.currentTarget.value === '') {
        this.setState({ amounts: { ...this.state.amounts, [productCode]: '' } })
      } else if (isNotNumber(e.currentTarget.value)) {
        this.setState({ amounts: { ...this.state.amounts, [productCode]: 0 } })
      } else {
        let value = parseInt(e.currentTarget.value || 0, 10);
        if (value > stockAmount) { value = stockAmount; }
        this.setState({ amounts: { ...this.state.amounts, [productCode]: value } })
      }
    }

  _price = (product) => {
    const { userClass } = this.props
    if (userClass === 2 && this.state.amounts[`${product.productCode}-${product.unitId}`] < (get(product, 'threshold') || 0)) {
      return get(product, 'alternativePrice')
    }
    return product[['retail', 'wholesale1', 'wholesale2', 'wholesale3', 'wholesale4'][userClass]] || 0
  }

  _onEnter = (e) => {
    if (e.keyCode === 13) this._onSave()
  }

  _renderDesktopProduct = ({ productCode, unitId, productName, imagePath, unit, outOfStock, ...rest }) => {
    const productKey = `${productCode}-${unitId}`
    const regex = unit.match(/\[(.*?)\]/);
    const perUnit = get(regex, 1) ? regex[1] : 1;
    const stockAmount = Math.floor(rest.stockAmount / perUnit);

    return (
      <tr key={productKey}>
        <td className='text-center'>
          <input type='checkbox'
            checked={this.state.selectedProductKeys.indexOf(productKey) > -1}
            onChange={() => this._onSelectProduct(productKey)} />
        </td>
        <td className='text-center'>{productCode}</td>
        <td>
          <img className={classes.icon} src='/images/icon-camera.png' alt='icon'
            onMouseEnter={() => this._onMouseEnter(productKey)}
            onMouseLeave={() => this._onMouseLeave(productKey)} />
          <Link to={`/products/${productCode}`}>{productName}</Link>
          <div className={`${classes.imageBox} ${this.state[`${productKey}-hovered`] && classes.active}`}>
            <img className={classes.thumbnail} src={imagePath} alt='thumbnail' />
          </div>
        </td>
        <td className='text-center'>
          {outOfStock ? (
            <div className='strike-through'>สินค้าหมดชั่วคราว</div>
          ) : (
            <span>
              <input type='text' value={this.state.amounts[productKey]}
                onKeyDown={this._onEnter}
                onChange={this._onAmountChange(productKey, stockAmount)}
                onBlur={this._onSave} />
              <span>/{stockAmount}</span>
            </span>
          )}
        </td>
        <td className='text-center'>{unit}</td>
        <td className='text-center'>{formatMoney(this._price({ productCode, ...rest }))}</td>
        <td className='text-center'>{outOfStock ? 0 : formatMoney(this._price({ productCode, unitId, ...rest }) * this.state.amounts[`${productCode}-${unitId}`])}</td>
      </tr>
    )
  }

  _renderMobileProduct = ({ productCode, unitId, productName, imagePath, giveaway, unit, amount, outOfStock, insufficient, ...rest }) => {
    const productKey = `${productCode}-${unitId}`
    const regex = unit.match(/\[(.*?)\]/);
    const perUnit = get(regex, 1) ? regex[1] : 1;
    const stockAmount = Math.floor(rest.stockAmount / perUnit)
    return (
      <div key={productKey} className={classes.row}
        onClick={() => this.props.openProductModal(productKey, this.state.amounts[productKey] || 0)}>
        <Col index={1}>{productCode}</Col>
        <Col index={2}>
          {productName}
          {insufficient && <div style={{ color: '#ed1c24' }}>*ของแถมมีจำนวนจำกัด ทำให้ได้รับของแถมไม่ครบตามโปรโมชั่น*</div>}
        </Col>
        {outOfStock ? (
          <Col className='strike-through' index={3}>สินค้าหมดชั่วคราว</Col>
        ) : giveaway ? <Col index={3} giveaway>{amount} {unit}</Col> : (
          <Col index={3}>{this.state.amounts[productKey]}/{stockAmount} {unit}</Col>
        )}
        <Col index={4}>{giveaway ? '0.00' : formatMoney(this._price({ productCode, ...rest }))}</Col>
        <Col index={5}>{(outOfStock || giveaway) ? '0.00' : formatMoney(this._price({ productCode, ...rest }) * this.state.amounts[productKey] || 0)}</Col>
      </div>
    )
  }

  // แยกสินค้าเป็น 2 กลุ่ม: จัดส่งฟรี (freeDelivery) และมีค่าจัดส่ง
  _splitByDelivery = (products) => ({
    freeDelivery: products.filter(product => Boolean(product.freeDelivery)),
    paidDelivery: products.filter(product => !product.freeDelivery)
  })

  // Render รายการสินค้าฝั่ง desktop แบบแยกกลุ่มจัดส่งฟรี / มีค่าจัดส่ง
  _renderDesktopRows = (products) => {
    const { freeDelivery, paidDelivery } = this._splitByDelivery(products)
    const rows = []
    if (freeDelivery.length) {
      rows.push(
        <tr key='group-free' className={classes.groupHeader}>
          <td colSpan='7'>รายการส่งฟรี</td>
        </tr>
      )
      freeDelivery.forEach(product => rows.push(this._renderDesktopProduct(product)))
    }
    if (paidDelivery.length) {
      rows.push(
        <tr key='group-paid' className={classes.groupHeader}>
          <td colSpan='7'>รายการมีค่าส่ง</td>
        </tr>
      )
      paidDelivery.forEach(product => rows.push(this._renderDesktopProduct(product)))
    }
    return rows
  }

  // Render รายการสินค้าฝั่ง mobile แบบแยกกลุ่มจัดส่งฟรี / มีค่าจัดส่ง
  _renderMobileRows = (products) => {
    const { freeDelivery, paidDelivery } = this._splitByDelivery(products)
    const rows = []
    if (freeDelivery.length) {
      rows.push(<div key='group-free' className={classes.mobileGroupHeader}>รายการส่งฟรี</div>)
      freeDelivery.forEach(product => rows.push(this._renderMobileProduct(product)))
    }
    if (paidDelivery.length) {
      rows.push(<div key='group-paid' className={classes.mobileGroupHeader}>รายการมีค่าส่ง</div>)
      paidDelivery.forEach(product => rows.push(this._renderMobileProduct(product)))
    }
    return rows
  }

  _onModalAmountChange = (e, productCode) =>
    this.setState({ amounts: { ...this.state.amounts, [productCode]: e.target.value } })

  _onModalOpen = (currentProduct) => this.setState({
    modalVisible: !currentProduct.giveaway,
    currentProduct: currentProduct.giveaway ? this.state.currentProduct : currentProduct
  })
  _onModalClose = () => this.setState({
    modalVisible: false, currentProduct: null
  })

  _validateInput = () => {
    const { attempting } = this.props
    return !attempting && !hasError(['contactPersonName', 'address', 'mobile'].reduce((error, fieldName) => ({
      ...error, ...validateRequired(this.state[fieldName], fieldName)
    }), {}))
  }

  _onCheckout = () => {
    if (this.state.attempting) return;
    if (this._validateInput()) {
      const { attemptCheckout, cartOrderId } = this.props
      attemptCheckout({
        cartOrderId,
        deliveryName: this.state.contactPersonName,
        deliveryAddress: this.state.address,
        deliveryContactNumber: this.state.mobile,
        promoCode: this.state.code ? get(this.state, 'promocode.code') : null,
        promoDiscount: this.state.code ? get(this.state, 'promocode.discount') : null,
        promoUnit: this.state.code ? get(this.state, 'promocode.unit') : null,
        giveawayCode: this.state.giveawayCode
      })
    }
  }

  _onSave = () => {
    this._onModalClose()
    const { attemptSaveCart, cartProducts } = this.props
    attemptSaveCart(
      cartProducts.map(product => ({
        ...product, amount: this.state.amounts[`${product.productCode}-${product.unitId}`]
      }))
        .filter(product => product.amount)
    )
  }

  _onDelete = (productKey) => {
    this._onModalClose()
    const { attemptSaveCart, cartProducts } = this.props
    if (productKey) {
      attemptSaveCart(cartProducts.filter(product => `${product.productCode}-${product.unitId}` !== productKey))
    } else {
      attemptSaveCart(cartProducts.filter(product =>
        this.state.selectedProductKeys.indexOf(`${product.productCode}-${product.unitId}`) === -1
      ))
    }
    this.setState({ selectedProductKeys: [] })
  }

  _onCancel = () => {
    this.props.attemptSaveCart([])
  }

  _onOkay = () => this.context.router.replace('/');

  _onWishlist = (alreadyAdded) => {
    const { productCode } = this.state.currentProduct || {}
    this.setState({
      currentProduct: {
        ...this.state.currentProduct,
        userId: !alreadyAdded
      }
    })
    if (alreadyAdded) {
      this.props.attemptRemoveFromWishlist(productCode)
    } else {
      this.props.attemptAddToWishlist(productCode)
    }
  }

  _findOnePromocode = debounce((promocode) => {
    if (!promocode) return
    this.setState({ verifying: true, verified: false }, () =>
      apiFetch({
        endpoint: `/promocodes/${promocode}/verify`
      })
        .then((result) => this.setState({ verifying: false, promocode: result.promocode, useCount: result.count }))
    )
  }, 200);

  _onPromocodeChange = e => {
    const code = e.target.value.toUpperCase()
    this.setState({ code, promocode: code ? this.state.promocode : {} })
    this._findOnePromocode(code)
  }

  _notEnough = () => {
    return this.props.cartProducts.some(product => (product.amount * product.perUnit) > product.stockAmount);
  }

  _hasGiveaways = () => {
    const freeGiveaways = this.props.giveaways.filter(({ minimumSpend, minimumItem }) => !minimumSpend && !minimumItem)
    return this._getGiveaways().filter(({ amount }) => amount).length || freeGiveaways.length
  }

  _getGiveaways = () => {
    const { giveaways, cartProducts } = this.props
    const giveawayGroups = groupBy(giveaways.filter(({ minimumSpend, minimumItem }) => minimumSpend || minimumItem), 'giveawayCode')
    const products = clone(cartProducts)
    products.sort((p1, p2) => p1.productName.localeCompare(p2.productName))
    const productMap = products.reduce((map, product) => ({ ...map, [product.productCode]: product }), {})
    const perUnitMap = products.reduce((map, product) => ({ ...map, [`${product.productCode}-${product.unitId}`]: product.perUnit }), {})

    return Object.keys(giveawayGroups).map(key => {
      const giveawaysByCode = giveawayGroups[key]
      const { minimumSpend, minimumItem, giveaway, stockAmount, ...rest } = get(giveawaysByCode, 0) || {}
      if (minimumSpend) {
        const totalPrice = giveawaysByCode.reduce((totalPrice, { giveawayWhen }) => {
          const _price = this._price({ productCode: giveawayWhen, ...productMap[giveawayWhen] })
          const _amount = sumBy(Object.keys(this.state.amounts).filter(key => key.includes(giveawayWhen)), key => this.state.amounts[key])
          return totalPrice + _price * _amount
        }, 0)

        // console.log('minimumSpend', {
        //   ...rest,
        //   stockAmount,
        //   totalPrice,
        //   giveaway,
        //   minimumSpend,
        //   minimumItem,
        //   amount: totalPrice < minimumSpend ? 0 : Math.min(stockAmount, Math.floor(totalPrice / minimumSpend) * giveaway),
        //   insufficient: stockAmount < Math.floor(totalPrice * giveaway / minimumSpend)
        // });
        return {
          ...rest,
          minimumSpend,
          minimumItem,
          amount: totalPrice < minimumSpend ? 0 : Math.min(stockAmount, Math.floor(totalPrice / minimumSpend) * giveaway),
          insufficient: stockAmount < Math.floor(totalPrice * giveaway / minimumSpend)
        }
      } else if (minimumItem) {
        const totalItems = giveawaysByCode.reduce((totalItems, { giveawayWhen }) => {
          const _keys = Object.keys(this.state.amounts).filter(key => key.indexOf(giveawayWhen) === 0);
          const _amount = _keys.reduce((sum, _key) => sum + this.state.amounts[_key] * perUnitMap[_key], 0);
          // console.log('totalItems', giveawayWhen, this.state.amounts, _keys, _amount);
          return totalItems + _amount
        }, 0);

        // console.log('minimumItems', {
        //   ...rest,
        //   stockAmount,
        //   totalItems,
        //   giveaway,
        //   minimumSpend,
        //   minimumItem,
        //   amount: totalItems < minimumItem ? 0 : Math.min(stockAmount, Math.floor(totalItems / minimumItem) * giveaway),
        //   insufficient: stockAmount < Math.floor(totalItems * giveaway / minimumItem)
        // });

        return {
          ...rest,
          minimumSpend,
          minimumItem,
          amount: totalItems < minimumItem ? 0 : Math.min(stockAmount, Math.floor(totalItems / minimumItem) * giveaway),
          insufficient: stockAmount < Math.floor(totalItems * giveaway / minimumItem)
        }
      }

      return { ...rest, minimumSpend, minimumItem, amount: 0, insufficient: false }
    })
  }

  render() {
    const { cartProducts, attempting, cartOrderId, userClass, giveaways } = this.props
    const products = clone(cartProducts).filter(_product => this._price(_product) > 0)
    products.sort((p1, p2) => p1.productName.localeCompare(p2.productName))
    const totalPrice = products.reduce((totalPrice, product) =>
      totalPrice + (product.outOfStock ? 0 : (this.state.amounts[`${product.productCode}-${product.unitId}`] * this._price(product)))
      , 0)
    const { code, unit, discount, minimumSpend, quota, validFrom, validUntil } = this.state.promocode || {}
    const today = moment().format('YYYY-MM-DD')
    let inRange = true
    if (validFrom && validFrom.localeCompare(today) === 1) { inRange = false }
    if (validUntil && today.localeCompare(validUntil) === 1) { inRange = false }
    const promoValid = code && minimumSpend <= totalPrice && (!quota || this.state.useCount < quota) && inRange
    const finalPrice = totalPrice - (promoValid ? (unit === 'baht' ? discount : totalPrice * discount / 100) : 0)
    // เมื่อยอดสุทธิหลังหักส่วนลดถึงเกณฑ์ ให้แยกแสดงสินค้าเป็นกลุ่มจัดส่งฟรี / มีค่าจัดส่ง
    const splitByDelivery = finalPrice >= FREE_DELIVERY_THRESHOLD
    const giveawaysByCode = this._getGiveaways().sort((a, b) => a.productName.localeCompare(b.productName)).slice()
    // console.log({ giveawaysByCode });
    const freeGiveaways = giveaways.filter(({ minimumSpend, minimumItem }) => !minimumSpend && !minimumItem)

    return (
      <section id='cart' className={classes.cart}>
        <Helmet title='ตะกร้าสินค้า' />
        {(this.state.step === 1) && (
          <section>
            <Heading>ตะกร้าสินค้า</Heading>
            <div className={classes.cartLeft}>
              <table className={classes.cartTable}>
                <thead>
                  <tr>
                    <td className='text-center'>
                      <input type='checkbox'
                        onChange={() =>
                          this.setState({
                            selectedProductKeys: this.state.selectedProductKeys.length === products.length
                              ? [] : products.map(product => `${product.productCode}-${product.unitId}`)
                          })
                        }
                        checked={this.state.selectedProductKeys.length === products.length && products.length} />
                    </td>
                    <td className='text-center'>รหัสสินค้า</td>
                    <td className='text-center'>ชื่อสินค้า</td>
                    <td className='text-center'>จำนวน/คลัง</td>
                    <td className='text-center'>หน่วย</td>
                    <td className='text-center'>ราคา</td>
                    <td className='text-center'>ราคาสุทธิ</td>
                  </tr>
                </thead>
                <tbody>
                  {splitByDelivery
                    ? this._renderDesktopRows(products)
                    : products.map(this._renderDesktopProduct)}
                  {!products.length && (
                    <tr>
                      <td colSpan='7' className='text-center'>ไม่มีสินค้าในตะกร้า</td>
                    </tr>
                  )}
                  <tr>
                    <td colSpan='3' className='text-right'>
                      <span className={classes.totalPrice}>รวม</span>
                    </td>
                    <td />
                    <td />
                    <td />
                    <td className='text-center'>{formatMoney(totalPrice)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className={classes.cartRight}>
              <div className={classes.summaryBox}>
                <div className={classes.inputContainer}>
                  <input type='text' className={this.state.code ? (promoValid ? classes.valid : classes.invalid) : null} placeholder='ใส่ โค้ดส่วนลด ที่นี่' value={this.state.code} onChange={this._onPromocodeChange} />
                </div>
                {this.state.code && !code && <div className={classes.promoError}>ขออภัย โค้ดส่วนลด ไม่ถูกต้อง</div>}
                {code && minimumSpend > totalPrice && <div className={classes.promoError}>ยอดซื้อขั้นต่ำที่ใช้โค้ดส่วนลดนี้ได้คือ {formatMoney(minimumSpend)} บาท</div>}
                {code && (quota && this.state.useCount >= quota) && <div className={classes.promoError}>ขออภัยท่านใช้โค้ดส่วนลดเกินจำนวนครั้งแล้ว</div>}
                {code && !inRange && <div className={classes.promoError}>สิ้นสุดระยะเวลาการใช้โค้ดส่วนลดนี้แล้ว</div>}
                <div className={classes.priceSummaryContainer}>
                  {this.state.code && promoValid && (
                    <section>
                      <div className={classes.price}>
                        <div className={classes.label}>รวม</div>
                        <div className={classes.value}>{formatMoney(totalPrice)} บาท</div>
                      </div>
                      <div className={classes.discount}>
                        <div className={classes.label}>ส่วนลด{unit === 'percent' ? ` (${discount}%)` : ''}</div>
                        <div className={classes.value}>- {formatMoney(unit === 'percent' ? (discount * totalPrice / 100) : discount)} บาท</div>
                      </div>
                    </section>
                  )}
                  <div className={classes.total}>
                    <div className={classes.label}>รวม</div>
                    <div className={classes.value}>{formatMoney(finalPrice)} บาท</div>
                  </div>
                  <div
                    className={`${classes.btnContinue} ${!get(products, 0) && classes.disabled}`}
                    onClick={get(products, 0)
                      ? (this._notEnough() ? () => this.setState({ notEnough: true }) : () => this.setState({ step: this._hasGiveaways() ? 2 : 3, code: promoValid ? this.state.code : null }))
                      : () => { }}>
                    <span>ดำเนินการต่อ</span>
                  </div>
                </div>
              </div>
            </div>
            <div className='desktop'>
              <div className={classes.colLeft}>
                <div className={`${classes.button} ${classes.btnDelete} ${!this.state.selectedProductKeys.length && classes.disabled}`} onClick={() => this._onDelete()}>
                  <img src='/images/icon-bin-blue.png' alt='icon-bin-blue' width='16' height='22' />
                  <span>ลบรายการสินค้า</span>
                </div>
                <div className={`${classes.button} ${classes.btnMoreProducts}`} onClick={() => this.context.router.push('/products')}>
                  <img src='/images/icon-orange-left.png' alt='icon-orange-left' width='25' height='14' />
                  <span>เลือกสินค้าเพิ่ม</span>
                </div>
                <div className={`${classes.button} ${classes.btnCancel}`} onClick={this._onCancel}>
                  <img src='/images/icon-bin-red.png' alt='icon-bin-red' />
                  <span>ยกเลิกการสั่งซื้อ</span>
                </div>
              </div>
            </div>

            <div className={classes.table}>
              <div className={classes.row}>
                <Col index={1}>รหัสสินค้า</Col>
                <Col index={2}>ชื่อสินค้า</Col>
                <Col index={3}>จำนวน</Col>
                <Col index={4}>ราคา</Col>
                <Col index={5}>ราคาสุทธิ</Col>
              </div>
              {splitByDelivery
                ? this._renderMobileRows(products)
                : products.map(this._renderMobileProduct)}
              {!products.length && (
                <div className={classes.row}>
                  <div className='text-center'>ไม่มีสินค้าในตะกร้า</div>
                </div>
              )}
            </div>
            <div className='mobile'>
              <div className={classes.mobileButtons}>
                <div className={`${classes.button} ${classes.btnMoreProducts}`} onClick={() => this.context.router.push('/products')}>
                  <img src='/images/icon-orange-left.png' alt='icon-orange-left' width='25' height='14' />
                  <span>เลือกสินค้าเพิ่ม</span>
                </div>
                <div className={`${classes.button} ${classes.btnCancel}`} onClick={this._onCancel}>
                  <img src='/images/icon-bin-red.png' alt='icon-bin-red' />
                  <span>ยกเลิกการสั่งซื้อ</span>
                </div>
              </div>
              <div className={classes.priceSummary}>
                <div>จำนวนรวมทั้งสิ้น</div>
                <span className={classes.priceValue}>{formatMoney(totalPrice)}</span>
                <span style={{ marginLeft: 10 }}>บาท</span>
              </div>
              <div className={classes.btnCheckout}
                onClick={get(products, 0)
                  ? (this._notEnough() ? () => this.setState({ notEnough: true }) : () => this.setState({ step: this._hasGiveaways() ? 2 : 3, code: promoValid ? this.state.code : null }))
                  : () => { }}>
                <span>ดำเนินการต่อ</span>
              </div>
            </div>
          </section>
        )}
        {this.state.notEnough && (
          <section>
            <div className='desktop overlay'>
              <div className='overlay-cell'>
                <div className='lightbox' style={{ paddingBottom: 30, height: 'auto' }}>
                  <div className='line1'>
                    <span>รายการสินค้าไม่เพียงพอ</span>
                  </div>
                  <div className='line2'>
                    {this.props.cartProducts.filter(product => (product.amount * product.perUnit) > product.stockAmount).map((product, index) => {
                      return (
                        <div key={index}>
                          {product.productCode}: {product.productName} สินค้ามีไม่เพียงพอ
                        </div>
                      )
                    })}
                  </div>
                  <div className='line2'>กรุณาแก้ไขจำนวนสินค้าก่อนดำเนินการใหม่อีกครั้ง</div>
                  <div className='btn-close' onClick={() => this.setState({ notEnough: false })}>X</div>
                  <div className='btn-ok' style={{ margin: 'initial', marginTop: 20 }} onClick={() => this.setState({ notEnough: false })}>
                    <span>ตกลง</span>
                  </div>
                </div>
              </div>
            </div>
            <div className={`mobile ${classes.notEnough}`}>
              <Heading>รายการสินค้าไม่เพียงพอ</Heading>
              <div className={classes.line}>
                {this.props.cartProducts.filter(product => (product.amount * product.perUnit) > product.stockAmount).map((product, index) => {
                  return (
                    <div key={index}>
                      {product.productCode}: {product.productName} สินค้ามีไม่เพียงพอ
                    </div>
                  )
                })}
              </div>
              <div className={classes.line}>กรุณาแก้ไขจำนวนสินค้าก่อนดำเนินการใหม่อีกครั้ง</div>
              <div className={classes.btnCheckout} style={{ margin: '20px auto' }} onClick={() => this.setState({ notEnough: false })}>
                <span>ตกลง</span>
              </div>
            </div>
          </section>
        )}
        {/* Step 2 - รายการแถมจากโปรโมชั่น */}
        {this.state.step === 2 && (
          <section>
            <div className='desktop overlay'>
              <div className='overlay-cell'>
                <div className='lightbox' style={{ height: '90vh', width: 510, overflow: 'auto', padding: 10 }}>
                  <div className='btn-close' onClick={() => this.setState({ step: 1 })}>X</div>
                  {Boolean(giveawaysByCode.filter(({ amount, insufficient }) => amount > 0).length) && (
                    <section className={classes.giveaways}>
                      <Heading>รายการแถมจากโปรโมชั่น</Heading>
                      <table className={classes.giveawayTable}>
                        <thead>
                          <tr>
                            <td className='text-center'>รหัสสินค้า</td>
                            <td className='text-center'>ชื่อสินค้า</td>
                            <td className='text-center'>จำนวน</td>
                            <td className='text-center'>หน่วย</td>
                            <td className='text-center'>ราคา</td>
                            <td className='text-center'>ราคาสุทธิ</td>
                          </tr>
                        </thead>
                        <tbody>
                          {giveawaysByCode.filter(({ amount }) => amount > 0)
                            .map(({ giveawayCode, insufficient, amount, productName, imagePath, unit }) => {
                              return amount ? (
                                <tr>
                                  <td className='text-center'>
                                    {giveawayCode}
                                  </td>
                                  <td>
                                    <img className={classes.icon} src='/images/icon-camera.png' alt='icon'
                                      onMouseEnter={() => this._onMouseEnter(giveawayCode)}
                                      onMouseLeave={() => this._onMouseLeave(giveawayCode)} />
                                    {productName}
                                    {insufficient && <div style={{ color: '#ed1c24' }}>*ของแถมมีจำนวนจำกัด ทำให้ได้รับของแถมไม่ครบตามโปรโมชั่น*</div>}
                                    <div className={`${classes.imageBox} ${this.state[`${giveawayCode}-hovered`] && classes.active}`}>
                                      <img className={classes.thumbnail} src={imagePath} alt='thumbnail' />
                                    </div>
                                  </td>
                                  <td className='text-center'>
                                    {amount}
                                  </td>
                                  <td className='text-center'>
                                    {unit}
                                  </td>
                                  <td className='text-center'>ฟรี</td>
                                  <td className='text-center'>ฟรี</td>
                                </tr>
                              ) : null
                            })}
                        </tbody>
                      </table>
                    </section>
                  )}
                  {get(freeGiveaways, 0) && (
                    <section>
                      <Heading>เลือกสินค้าแถมฟรี ทุกการสั่งซื้อ</Heading>
                      <div>
                        {freeGiveaways.map(giveaway =>
                          <div key={giveaway.giveawayCode} className={classes.giveawayItemWrapper}>
                            <GiveawayItem product={giveaway}
                              active={giveaway.giveawayCode === this.state.giveawayCode}
                              onClick={() => this.setState({ giveawayCode: giveaway.giveawayCode })} />
                          </div>
                        )}
                      </div>
                    </section>
                  )}
                  <div className={`${classes.btnCheckout} ${get(freeGiveaways, 0) && !this.state.giveawayCode && classes.disabled}`}
                    onClick={get(freeGiveaways, 0) && !this.state.giveawayCode ? () => { } : () => this.setState({ step: 3 })}>
                    <span>ดำเนินการต่อ</span>
                  </div>
                </div>
              </div>
            </div>
            <div className='mobile'>
              {Boolean(giveawaysByCode.filter(({ amount }) => amount > 0).length) && (
                <section className={classes.giveaways}>
                  <Heading>รายการแถมจากโปรโมชั่น</Heading>
                  <div className={classes.table}>
                    <div className={classes.row}>
                      <Col index={1}>รหัสสินค้า</Col>
                      <Col index={2}>ชื่อสินค้า</Col>
                      <Col index={3}>จำนวน</Col>
                      <Col index={4}>ราคา</Col>
                      <Col index={5}>ราคาสุทธิ</Col>
                    </div>
                    {giveawaysByCode
                      .filter(({ amount }) => amount > 0)
                      .map(({ giveawayCode, amount, ...rest }) => ({ productCode: giveawayCode, amount, giveaway: true, ...rest }))
                      .map(this._renderMobileProduct)}
                    {!products.length && (
                      <div className={classes.row}>
                        <div className='text-center'>ไม่มีสินค้าในตะกร้า</div>
                      </div>
                    )}
                  </div>
                  <table className={classes.giveawayTable}>
                    <thead>
                      <tr>
                        <td className='text-center'>รหัสสินค้า</td>
                        <td className='text-center'>ชื่อสินค้า</td>
                        <td className='text-center'>จำนวน</td>
                        <td className='text-center'>หน่วย</td>
                        <td className='text-center'>ราคา</td>
                        <td className='text-center'>ราคาสุทธิ</td>
                      </tr>
                    </thead>
                    <tbody>
                      {giveawaysByCode.filter(({ amount }) => amount > 0)
                        .map(({ giveawayCode, insufficient, amount, productName, imagePath, unit }) => {
                          return amount ? (
                            <tr>
                              <td className='text-center'>
                                {giveawayCode}
                              </td>
                              <td>
                                <img className={classes.icon} src='/images/icon-camera.png' alt='icon'
                                  onMouseEnter={() => this._onMouseEnter(giveawayCode)}
                                  onMouseLeave={() => this._onMouseLeave(giveawayCode)} />
                                {productName}
                                {insufficient && <div style={{ color: '#ed1c24' }}>*ของแถมมีจำนวนจำกัด ทำให้ได้รับของแถมไม่ครบตามโปรโมชั่น*</div>}
                                <div className={`${classes.imageBox} ${this.state[`${giveawayCode}-hovered`] && classes.active}`}>
                                  <img className={classes.thumbnail} src={imagePath} alt='thumbnail' />
                                </div>
                              </td>
                              <td className='text-center'>
                                {amount}
                              </td>
                              <td className='text-center'>
                                {unit}
                              </td>
                              <td className='text-center'>ฟรี</td>
                              <td className='text-center'>ฟรี</td>
                            </tr>
                          ) : null
                        })}
                    </tbody>
                  </table>
                </section>
              )}
              {get(freeGiveaways, 0) && (
                <section>
                  <Heading>เลือกสินค้าแถมฟรี ทุกการสั่งซื้อ</Heading>
                  <div>
                    {freeGiveaways.map(giveaway =>
                      <div key={giveaway.giveawayCode} className={classes.giveawayItemWrapper}>
                        <GiveawayItem product={giveaway}
                          active={giveaway.giveawayCode === this.state.giveawayCode}
                          onClick={() => this.setState({ giveawayCode: giveaway.giveawayCode })} />
                      </div>
                    )}
                  </div>
                </section>
              )}
              <div style={{ margin: 10 }}>
                <div className={`${classes.btnContinue} ${get(freeGiveaways, 0) && !this.state.giveawayCode && classes.disabled}`}
                  onClick={get(freeGiveaways, 0) && !this.state.giveawayCode ? () => { } : () => this.setState({ step: 3 })}>
                  <span>ดำเนินการต่อ</span>
                </div>
                <div className={classes.btnBack} onClick={() => this.setState({ step: 1 })}>
                  <span>ย้อนกลับ</span>
                </div>
              </div>
            </div>
          </section>
        )}
        {/* Step 3 - ข้อมูลการจัดส่ง (Desktop), ใส่โค้ดส่วนลด (Mobile) */}
        {this.state.step === 3 && !this.state.checkedOut && (
          <section>
            <div className='desktop overlay'>
              <div className='overlay-cell'>
                <div className='lightbox' style={{ height: '90vh', width: 510, overflow: 'auto' }}>
                  <div className='btn-close' onClick={() => this.setState({ step: 1 })}>X</div>
                  <div className={classes.deliveryInfo}>
                    <h2>ข้อมูลการจัดส่ง</h2>
                    <div className={classes.checkboxWrapper}>
                      <input type='checkbox'
                        checked={this.state.checked}
                        onChange={() => this.setState({ checked: !this.state.checked })} />
                      <span>ใช้ข้อมูลของข้อมูลสมาชิก</span>
                    </div>
                    <FormGroup title='ชื่อ'>
                      <input type='text'
                        value={this.state.checked ? this.props.contactPersonName : this.state.contactPersonName}
                        disabled={this.state.checked}
                        onChange={e => this.setState({ contactPersonName: e.target.value })} />
                    </FormGroup>
                    <FormGroup title='ที่อยู่'>
                      <textarea
                        value={this.state.checked ? this.props.address : this.state.address}
                        disabled={this.state.checked}
                        onChange={e => this.setState({ address: e.target.value })} />
                    </FormGroup>
                    <FormGroup title='เบอร์ติดต่อ'>
                      <input type='text'
                        value={this.state.checked ? this.props.mobile : this.state.mobile}
                        disabled={this.state.checked}
                        onChange={e => this.setState({ mobile: e.target.value })} />
                    </FormGroup>
                    <div className={classes.btnCheckout} style={{ position: 'absolute', left: 30, width: 192 }} onClick={() => this.setState({ step: this._hasGiveaways() ? 2 : 1 })}>
                      ย้อนกลับ
                    </div>
                    <div className={classes.btnCheckout} onClick={this._onCheckout}>
                      {attempting ? <Loader /> : <span>ยืนยันการสั่งซื้อ</span>}
                    </div>
                  </div>
                  {this.state.checkoutError || ''}
                </div>
              </div>
            </div>
            <div className='mobile'>
              <div className={classes.summaryBox}>
                <div className={classes.inputContainer}>
                  <input type='text' className={this.state.code ? (promoValid ? classes.valid : classes.invalid) : null} placeholder='ใส่ โค้ดส่วนลด ที่นี่' value={this.state.code} onChange={this._onPromocodeChange} />
                </div>
                {this.state.code && !code && <div className={classes.promoError}>ขออภัย โค้ดส่วนลด ไม่ถูกต้อง</div>}
                {code && minimumSpend > totalPrice && <div className={classes.promoError}>ยอดซื้อขั้นต่ำที่ใช้โค้ดส่วนลดนี้ได้คือ {formatMoney(minimumSpend)} บาท</div>}
                {code && (quota && this.state.useCount >= quota) && <div className={classes.promoError}>ขออภัยท่านใช้โค้ดส่วนลดเกินจำนวนครั้งแล้ว</div>}
                {code && !inRange && <div className={classes.promoError}>สิ้นสุดระยะเวลาการใช้โค้ดส่วนลดนี้แล้ว</div>}
                <div className={classes.priceSummaryContainer}>
                  {this.state.code && promoValid && (
                    <section>
                      <div className={classes.price}>
                        <div className={classes.label}>รวม</div>
                        <div className={classes.value}>{formatMoney(totalPrice)} บาท</div>
                      </div>
                      <div className={classes.discount}>
                        <div className={classes.label}>ส่วนลด{unit === 'percent' ? ` (${discount}%)` : ''}</div>
                        <div className={classes.value}>- {formatMoney(unit === 'percent' ? (discount * totalPrice / 100) : discount)} บาท</div>
                      </div>
                    </section>
                  )}
                  <div className={classes.total}>
                    <div className={classes.label}>รวม</div>
                    <div className={classes.value}>{formatMoney(finalPrice)} บาท</div>
                  </div>
                  <div className={classes.btnContinue} onClick={() => this.setState({ step: 4, code: promoValid ? this.state.code : null })}>
                    <span>ดำเนินการต่อ</span>
                  </div>
                  <div className={classes.btnBack} onClick={() => this.setState({ step: this._hasGiveaways() ? 2 : 1 })}>
                    <span>ย้อนกลับ</span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
        {/* Step 4 - ข้อมูลการจัดส่ง (Mobile) */}
        {this.state.step === 4 && !this.state.checkedOut && (
          <div className={`${classes.deliveryInfo} mobile`}>
            <h2>ข้อมูลการจัดส่ง</h2>
            <div className={classes.checkboxWrapper}>
              <input type='checkbox'
                checked={this.state.checked}
                onChange={() => this.setState({ checked: !this.state.checked })} />
              <span>ใช้ข้อมูลของข้อมูลสมาชิก</span>
            </div>
            <FormGroup title='ชื่อ'>
              <input type='text'
                value={this.state.checked ? this.props.contactPersonName : this.state.contactPersonName}
                disabled={this.state.checked}
                onChange={e => this.setState({ contactPersonName: e.target.value })} />
            </FormGroup>
            <FormGroup title='ที่อยู่'>
              <textarea
                value={this.state.checked ? this.props.address : this.state.address}
                disabled={this.state.checked}
                onChange={e => this.setState({ address: e.target.value })} />
            </FormGroup>
            <FormGroup title='เบอร์ติดต่อ'>
              <input type='text'
                value={this.state.checked ? this.props.mobile : this.state.mobile}
                disabled={this.state.checked}
                onChange={e => this.setState({ mobile: e.target.value })} />
            </FormGroup>
            <div className={classes.btnContinue} onClick={this._onCheckout}>
              {attempting ? <Loader /> : <span>ยืนยันการสั่งซื้อ</span>}
            </div>
            <div className={classes.btnBack} onClick={() => this.setState({ step: 3 })}>
              <span>ย้อนกลับ</span>
            </div>
          </div>
        )}
        {this.state.checkedOut && (
          <div className='overlay'>
            <div className='overlay-cell'>
              <div className='lightbox'>
                <div className='line1'>
                  <span>ท่านได้ยืนยันการสั่งซื้อเสร็จสมบูรณ์แล้ว</span>
                </div>
                <div className='line2'>
                  <div>
                    <span className='red'>รหัสใบสั่งซื้อของคุณคือ {cartOrderId}</span>
                  </div>
                  <div>หลังจากจัดสินค้าเรียบร้อยแล้ว เจ้าหน้าที่จะแจ้งท่านทราบถึง สินค้าที่ไม่ได้รับ</div>
                  <div>และสรุปยอดเงินในการสั่งซื้ออีกครั้งทางไลน์หรือทางโทรศัพท์ค่ะ</div>
                </div>
                <img className='mascot' src='/images/mascot.png' alt='mascot' />
                <div className='btn-close' onClick={this._onOkay}>X</div>
                <div className='btn-ok' onClick={this._onOkay}>
                  <span>ตกลง</span>
                </div>
              </div>
            </div>
          </div>
        )}
        <ProductModal
          visible={this.state.modalVisible}
          product={this.state.currentProduct}
          userClass={userClass}
          amount={this.state.amounts[get(this.state, 'currentProduct.productCode')]}
          onAmountChange={this._onModalAmountChange}
          onClose={this._onModalClose}
          onDelete={this._onDelete}
          onAddToCart={this._onSave}
          onWishlist={this._onWishlist} />
      </section>
    )
  }
}

const leftMap = [15, 45, 60, 80, 100]
const Col = ({ className, style, children, index, giveaway }) =>
  <div className={`${classes.col} ${className} ${giveaway && classes.giveaway}`} style={style}>
    {children}
    <div style={{ left: `${leftMap[index - 1]}%` }} className={classes.colBorder} />
  </div>

export default Cart



// WEBPACK FOOTER //
// ./src/containers/User/Cart/Cart.js